// src/components/Navbar.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { clearHierarchyCache, useDataLayer, useCachedHierarchy } from '@quantiix/data-layer';
import { useAuth } from '@/contexts/AuthContext';
import { getFirstPublicView, getFirstMachine } from '@/utils/hierarchyHelpers';
import { MICRO_FRONTENDS } from '@/config/MicroFrontend';
import { useToast } from '@/contexts/ToastContext';
import NavbarItem from './NavbarItem';
import { createLogger } from '@/utils/logger';
import styles from './Navbar.module.css';

import CompanyLogoRaw from '@/assets/logos/Q-full-logo.svg?raw';

import { useTranslation } from '../i18n';

const logger = createLogger('Shell::Navbar');

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { companyId: activeCompanyId, setScope, navigationSource } = useDataLayer();
    const { logout, user, hasFeature, hasAnyPermission, primaryCompany, permissions } = useAuth();
    const { addToast } = useToast();
    const navbarRef = useRef(null);

    const [isOpen, setIsOpen] = useState(false);
    const [hoverItem, setHoverItem] = useState(null);
    const [isHovered, setHovered] = useState(false);
    const [hoverTrigger, setHoverTrigger] = useState(false);
    const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);

    const { t } = useTranslation('shell');

    // Determine which company to use
    const targetCompanyId = activeCompanyId || primaryCompany?.id;

    // Read hierarchy from cache (populated by Dashboard MFE)
    const hierarchy = useCachedHierarchy(targetCompanyId);

    const userInitials = (user?.firstName && user?.lastName)
        ? user.firstName[0] + user.lastName[0]
        : 'US';

    const account = t('navbar.account');
    const configModeLabel = t('navbar.configMode');

    const currentAppId = location.pathname.startsWith('/app/')
        ? location.pathname.split('/app/')[1].split('/')[0]
        : '';

    const isConfigActive = currentAppId === 'config';

    const hasConfigAccess = () => hasFeature('edit_mode');

    // Determine if an MFE should show as selected in navbar
    const isAppSelected = useCallback((appKey) => {
        // PublicView/Machine: only selected if navigated via navbar
        if (appKey === 'publicview' || appKey === 'machine') {
            return currentAppId === appKey && navigationSource === 'navbar';
        }

        // Dashboard: also selected when on publicview/machine via drill-down
        if (appKey === 'dashboard') {
            if (currentAppId === 'dashboard') {
                return true;
            }
            // Drill-down to publicview/machine keeps dashboard selected
            if ((currentAppId === 'publicview' || currentAppId === 'machine') && navigationSource !== 'navbar') {
                return true;
            }
            return false;
        }

        // Other MFEs: selected if on their route
        return currentAppId === appKey;
    }, [currentAppId, navigationSource]);

    /**
     * Navigate to PublicView MFE
     */
    const navigateToPublicView = useCallback(() => {
        if (!targetCompanyId) {
            addToast({
                type: 'info',
                message: t('navbar.selectCompanyFirst'),
                duration: 3000
            });
            navigate('/app/dashboard');
            return;
        }

        if (!hierarchy) {
            // Hierarchy not cached - Dashboard hasn't loaded yet
            logger.warn('Hierarchy not cached, redirecting to dashboard');
            navigate('/app/dashboard');
            return;
        }

        const firstPV = getFirstPublicView(hierarchy);

        if (!firstPV) {
            addToast({
                type: 'warning',
                message: t('navbar.noPublicViewAvailable'),
                duration: 3000
            });
            return;
        }

        logger.info('Navigating to PublicView', { ouId: firstPV.ouId, ouName: firstPV.ouName });

        setScope({
            companyId: firstPV.companyId,
            factoryId: firstPV.factoryId,
            ouId: firstPV.ouId,
            navigationSource: 'navbar'
        });

        navigate(`/app/publicview/${firstPV.ouId}`);
    }, [targetCompanyId, hierarchy, setScope, navigate, addToast, t]);

    /**
     * Navigate to Machine MFE
     */
    const navigateToMachine = useCallback(() => {
        if (!targetCompanyId) {
            addToast({
                type: 'info',
                message: t('navbar.selectCompanyFirst'),
                duration: 3000
            });
            navigate('/app/dashboard');
            return;
        }

        if (!hierarchy) {
            logger.warn('Hierarchy not cached, redirecting to dashboard');
            navigate('/app/dashboard');
            return;
        }

        const firstMachine = getFirstMachine(hierarchy);

        if (!firstMachine) {
            addToast({
                type: 'warning',
                message: t('navbar.noMachineAvailable'),
                duration: 3000
            });
            return;
        }

        logger.info('Navigating to Machine', {
            machineId: firstMachine.machine.id,
            machineName: firstMachine.machine.name
        });

        setScope({
            companyId: firstMachine.companyId,
            factoryId: firstMachine.factoryId,
            ouId: firstMachine.ouId,
            machineId: firstMachine.machine.id,
            navigationSource: 'navbar'
        });

        navigate(`/app/machine/${firstMachine.machine.id}`);
    }, [targetCompanyId, hierarchy, setScope, navigate, addToast, t]);

    /**
     * Navigate to an MFE from NavBar
     */
    const navigateToApp = useCallback((appKey, path) => {
        // Check if user is an operator (operators shouldn't use this flow)
        const isOperator = permissions?.roles?.includes('operator');

        // Clear hierarchy cache for fresh start on dashboard
        if (appKey === 'dashboard' || appKey === null) {
            clearHierarchyCache(queryClient);
            setScope({ navigationSource: null });
            window.dispatchEvent(new CustomEvent('breadcrumb-update', { detail: null }));

            // Single-company non-operator user goes directly to their company
            if (appKey === 'dashboard' && primaryCompany?.id && permissions?.company?.length === 1 && !isOperator) {
                logger.info('Navigating single-company user to their company', {
                    companyId: primaryCompany.id,
                    companyName: primaryCompany.name
                });
                navigate(`/app/dashboard/company/${primaryCompany.id}`);
                return;
            }
        }

        // PublicView MFE
        if (appKey === 'publicview') {
            window.dispatchEvent(new CustomEvent('breadcrumb-update', { detail: null }));
            navigateToPublicView();
            return;
        }

        // Machine MFE
        if (appKey === 'machine') {
            window.dispatchEvent(new CustomEvent('breadcrumb-update', { detail: null }));
            navigateToMachine();
            return;
        }

        // Other MFEs
        setScope({ navigationSource: null });
        navigate(path);
    }, [queryClient, setScope, navigate, navigateToPublicView, navigateToMachine, primaryCompany, permissions]);

    const handleConfigToggle = () => {
        navigateToApp('config', isConfigActive ? '/' : '/app/config');
    };

    const handleMouseEnter = () => {
        setHovered(true);
        setTimeout(() => setHoverTrigger(!hoverTrigger), 300);
    };

    const handleMouseLeave = () => {
        setHovered(false);
        setIsOpen(false);
    };

    useEffect(() => {
        if (isHovered) {
            setIsOpen(true);
        }
    }, [hoverTrigger, isHovered]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const getNavbarClasses = () => {
        const classes = [styles.navbarContainer];
        if (isOpen) classes.push(styles.isOpen);
        if (isConfigActive) classes.push(styles.isConfigMode);
        return classes.join(' ');
    };

    const getUserCircleClasses = () => {
        const classes = [styles.userSettingsCircle];
        if (account === hoverItem) classes.push(styles.isHovered);
        if (isConfigActive) classes.push(styles.isConfigMode);
        return classes.join(' ');
    };

    const getDropdownClasses = () => {
        const classes = [styles.accountDropdown];
        if (isAccountDropdownOpen) classes.push(styles.isOpen);
        return classes.join(' ');
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (navbarRef.current && !navbarRef.current.contains(event.target)) {
                setIsAccountDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Visibility check supports two gating models:
    //   - requiredPermissions (array): role-based any-of (via hasAnyPermission)
    //   - requiredPermission  (string, legacy): feature-flag check (via hasFeature)
    const isMfeVisible = (app) => {
        if (Array.isArray(app.requiredPermissions) && app.requiredPermissions.length > 0) {
            return hasAnyPermission(app.requiredPermissions);
        }
        return hasFeature(app.requiredPermission);
    };

    const accessibleMFEs = Object.entries(MICRO_FRONTENDS)
        .filter(([key, app]) => key !== 'config' && app.showInNav && isMfeVisible(app))
        .sort(([, a], [, b]) => a.order - b.order);

    return (
        <nav
            ref={navbarRef}
            className={getNavbarClasses()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="navigation"
            aria-label={t('navbar.mainNavigation')}
        >
            <div
                className={`${styles.navbarLogo} ${isConfigActive ? styles.isConfigMode : ''}`}
                dangerouslySetInnerHTML={{ __html: CompanyLogoRaw }}
                onClick={() => navigateToApp(null, '/')}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') navigateToApp(null, '/');
                }}
                tabIndex={0}
                role="button"
                aria-label="Go to home"
            />

            <div className={styles.appsIconsContainer}>
                {accessibleMFEs.map(([key, app]) => {
                    const label = app.labelKey ? t(app.labelKey) : app.name;
                    return (
                        <NavbarItem
                            key={key}
                            isOpen={isOpen}
                            isConfigMode={isConfigActive}
                            dataId={`navbar_${key}`}
                            iconRest={app.iconRest}
                            iconHover={app.iconHover}
                            iconActive={app.iconActive}
                            appName={label}
                            onHover={setHoverItem}
                            isHovered={label === hoverItem}
                            isSelected={isAppSelected(key)}
                            onClick={() => navigateToApp(key, `/app/${key}`)}
                        />
                    );
                })}
            </div>

            <div className={styles.bottomIconsContainer}>
                {hasConfigAccess() && (
                    <NavbarItem
                        isOpen={isOpen}
                        isConfigMode={isConfigActive}
                        dataId="navbar_config_mode"
                        iconRest={MICRO_FRONTENDS.config.iconRest}
                        iconHover={MICRO_FRONTENDS.config.iconHover}
                        iconActive={MICRO_FRONTENDS.config.iconActive}
                        appName={configModeLabel}
                        onHover={setHoverItem}
                        isHovered={configModeLabel === hoverItem}
                        isSelected={isConfigActive}
                        onClick={handleConfigToggle}
                    />
                )}

                <div className={styles.accountSection}>
                    <NavbarItem
                        isOpen={isOpen}
                        isConfigMode={isConfigActive}
                        dataId="navbar_settings"
                        appName={account}
                        onHover={setHoverItem}
                        isHovered={account === hoverItem}
                        onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                    >
                        <div className={styles.iconContainer}>
                            <div className={getUserCircleClasses()} aria-label="User settings">
                                {userInitials}
                            </div>
                        </div>
                    </NavbarItem>

                    {isAccountDropdownOpen && (
                        <div className={getDropdownClasses()} role="menu">
                            <button
                                className={styles.dropdownItem}
                                onClick={() => {
                                    setIsAccountDropdownOpen(false);
                                    navigate('/app/settings');
                                }}
                                role="menuitem"
                            >
                                {t('navbar.menu.settings')}
                            </button>

                            {hasFeature('user_management') && (
                                <button
                                    className={styles.dropdownItem}
                                    onClick={() => {
                                        setIsAccountDropdownOpen(false);
                                        window.dispatchEvent(new CustomEvent('open-user-management'));
                                    }}
                                    role="menuitem"
                                >
                                    {t('navbar.menu.adminSettings')}
                                </button>
                            )}

                            <div className={styles.dropdownDivider} />

                            <button
                                className={styles.dropdownItem}
                                onClick={handleLogout}
                                role="menuitem"
                            >
                                {t('navbar.menu.logOut')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default Navbar;